import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Answer {
  text: string;
  info?: string;
}
interface Question {
  id: number;
  question: string;
  info: string;
  answers: Answer[];
  multiSelect?: boolean;
  showOtherInput?: boolean;
  condition?: (answers: Record<number, string[]>) => boolean;
}
// Define question IDs for reference
const Q_ORG_TYPE = 0;
const Q_ORG_SIZE = 1;
const Q_WEBSITE_TYPE = 2;
const Q_CURRENT_WEBSITE = 3;
const Q_GOAL = 4;
const Q_PAGES = 5;
const Q_REGULATIONS = 6;
const Q_SEO = 7;
const Q_CONTENT = 8;
const Q_HOSTING = 9;
const Q_TIMELINE = 10;
const Q_PRIORITY = 11;
const questions: Question[] = [{
  id: Q_ORG_TYPE,
  question: 'Wat voor organisatie bent u?',
  info: 'Various financial organizations have different requirements regarding appearance, content and legislation. By mapping this well, we tailor the website better to your branch and target group.',
  answers: [{
    text: 'Financieel adviseur (zzp)',
    info: 'Kleinschalige praktijk met focus op persoonlijke dienstverlening en vertrouwen'
  }, {
    text: 'Administratiekantoor',
    info: 'Professionele dienstverlening met nadruk op betrouwbaarheid en efficiency'
  }, {
    text: 'Accountantskantoor',
    info: 'Formele uitstraling met strikte naleving van beroepsregels en compliance'
  }, {
    text: 'Hypotheekadviseur',
    info: 'Consumentgerichte communicatie met duidelijke informatievoorziening'
  }, {
    text: 'Vermogensbeheerder',
    info: 'Premium uitstraling met focus op expertise en resultaten'
  }, {
    text: 'Consultancybureau (financieel / strategisch)',
    info: 'Zakelijke dienstverlening met nadruk op expertise en strategisch inzicht'
  }, {
    text: 'Anders',
    info: 'Andere financiële of zakelijke dienstverlening'
  }],
  showOtherInput: true
}, {
  id: Q_ORG_SIZE,
  question: 'Hoe groot is uw organisatie?',
  info: "De omvang van uw organisatie zegt veel over de benodigde structuur en schaalbaarheid van de website. Grotere organisaties vragen vaak om meer pagina's en extra aandacht voor betrouwbaarheid.",
  answers: [{
    text: 'Zelfstandig',
    info: 'Eenmanszaak of zelfstandige zonder personeel'
  }, {
    text: '2–5 medewerkers',
    info: 'Klein team met beperkte structuur'
  }, {
    text: '6–15 medewerkers',
    info: 'Middelgroot kantoor met meerdere specialisaties'
  }, {
    text: '15+ medewerkers',
    info: 'Grote organisatie met uitgebreide structuur en afdelingen'
  }]
}, {
  id: Q_WEBSITE_TYPE,
  question: 'Wat voor type website wilt u laten maken?',
  info: 'Het type website bepaalt de basis van de prijs. Een onepager is eenvoudiger dan een volledige website, terwijl een webshop extra techniek en onderhoud vereist.',
  answers: [{
    text: 'Onepager / landingspagina',
    info: 'Eenvoudige website op één pagina, perfect voor campagnes of startende ondernemers'
  }, {
    text: "Normale website (meerdere pagina's)",
    info: "Volledige website met meerdere pagina's, structuur en navigatie"
  }, {
    text: 'Webshop',
    info: 'Website met verkoopfunctionaliteit, producten en betalingen'
  }]
}, {
  id: Q_CURRENT_WEBSITE,
  question: 'Heeft u op dit moment een website?',
  info: 'The current state of your website determines where the most improvement is possible. Think about appearance, technology, speed, security and conversion.',
  answers: [{
    text: 'Nee, dit wordt mijn eerste website',
    info: 'Volledig nieuwe opzet zonder bestaande content of structuur'
  }, {
    text: 'Ja, maar deze is verouderd',
    info: 'Bestaande website die technisch of visueel niet meer voldoet'
  }, {
    text: 'Ja, maar voldoet niet meer aan wetgeving of uitstraling',
    info: 'Website die niet meer compliant is of professioneel genoeg oogt'
  }, {
    text: 'Ja, maar levert te weinig aanvragen op',
    info: 'Website die niet converteert of onvoldoende leads genereert'
  }]
}, {
  id: Q_GOAL,
  question: 'Wat is het belangrijkste doel van de nieuwe website?',
  info: 'Een website werkt het beste wanneer deze één of meerdere duidelijke doelen heeft. Dit helpt bij het bepalen van design, structuur en inhoud.',
  answers: [{
    text: 'Professionele uitstraling & vertrouwen',
    info: 'Focus op geloofwaardigheid en eerste indruk'
  }, {
    text: 'Meer aanvragen / leads',
    info: 'Conversie-optimalisatie en duidelijke call-to-actions'
  }, {
    text: 'Informatie voor bestaande klanten',
    info: 'Klantportaal, downloads en self-service mogelijkheden'
  }, {
    text: 'Autoriteit uitstralen binnen mijn vakgebied',
    info: 'Thought leadership, kennisdeling en positionering'
  }, {
    text: 'Voldoen aan wet- en regelgeving',
    info: 'Compliance met AFM, Wft en andere brancheregels'
  }],
  multiSelect: true
}, {
  id: Q_PAGES,
  question: "Welke pagina's verwacht u minimaal nodig te hebben?",
  info: "Het aantal en type pagina's bepalen de omvang en complexiteit van de website. Financiële websites bevatten vaak extra pagina's om vertrouwen en duidelijkheid te bieden.",
  answers: [{
    text: 'Home',
    info: 'Startpagina met overzicht en eerste indruk'
  }, {
    text: 'Diensten',
    info: 'Uitgebreide beschrijving van uw aanbod'
  }, {
    text: 'Over ons',
    info: 'Bedrijfsinformatie, missie en visie'
  }, {
    text: 'Contact',
    info: 'Contactformulier en bereikbaarheid'
  }, {
    text: 'Team / medewerkers',
    info: "Overzicht van uw team met foto's en expertise"
  }, {
    text: 'Klantportaal / login',
    info: 'Beveiligde omgeving voor klanten'
  }, {
    text: 'Blog / kennisbank',
    info: 'Artikelen en kennisdeling voor SEO en autoriteit'
  }, {
    text: 'Vacatures',
    info: 'Wervingspagina voor nieuwe medewerkers'
  }, {
    text: 'Overig',
    info: "Andere pagina's die u nodig heeft"
  }],
  multiSelect: true,
  showOtherInput: true,
  condition: answers => answers[Q_WEBSITE_TYPE]?.includes("Normale website (meerdere pagina's)")
}, {
  id: Q_REGULATIONS,
  question: 'Moet de website aan specifieke regels voldoen?',
  info: 'In de financiële sector gelden vaak strengere richtlijnen voor online communicatie. Door hier vooraf rekening mee te houden, voorkomen we aanpassingen achteraf.',
  answers: [{
    text: 'AFM / Wft-communicatie',
    info: 'Naleving van AFM-richtlijnen en Wft-verplichtingen'
  }, {
    text: 'Privacy & AVG',
    info: 'GDPR-compliance en privacyverklaring'
  }, {
    text: 'Disclaimer / risicovermelding',
    info: 'Wettelijk verplichte disclaimers en risicomeldingen'
  }, {
    text: 'Cookiebeleid',
    info: 'Cookie-consent en tracking volgens wetgeving'
  }, {
    text: 'Geen specifieke eisen',
    info: 'Geen bijzondere compliance-vereisten'
  }, {
    text: 'Overig',
    info: 'Andere specifieke regelgeving of branche-eisen'
  }],
  multiSelect: true,
  showOtherInput: true
}, {
  id: Q_SEO,
  question: 'Hoe belangrijk is vindbaarheid in Google voor u?',
  info: 'Goede vindbaarheid ontstaat niet vanzelf. Wij realiseren betere zichtbaarheid door het slim inzetten van SEO, afgestemd op uw diensten en doelgroep.',
  answers: [{
    text: 'Zeer belangrijk',
    info: 'SEO is cruciaal voor acquisitie en groei'
  }, {
    text: 'Belangrijk',
    info: 'Goede vindbaarheid draagt bij aan nieuwe klanten'
  }, {
    text: 'Enigszins belangrijk',
    info: 'Basis SEO is voldoende, geen actieve focus'
  }, {
    text: 'Niet belangrijk',
    info: 'Klanten komen via andere kanalen (netwerk, verwijzingen)'
  }]
}, {
  id: Q_CONTENT,
  question: 'Wilt u dat teksten door een specialist worden geschreven?',
  info: 'Heldere en correcte teksten zijn essentieel binnen de financiële sector. Professioneel geschreven content vergroot vertrouwen en conversie.',
  answers: [{
    text: 'Volledig laten schrijven (inhoudelijk correct, juridisch zorgvuldig en passend binnen financiële communicatie)',
    info: 'Wij schrijven alle content professioneel en compliant'
  }, {
    text: 'Samen herschrijven en optimaliseren',
    info: 'Wij verbeteren uw teksten voor leesbaarheid en SEO'
  }, {
    text: 'Nee, wij leveren alle teksten zelf aan',
    info: 'U schrijft alle content zelf en levert deze aan'
  }]
}, {
  id: Q_HOSTING,
  question: 'Hoe wilt u dat hosting, onderhoud en beveiliging worden geregeld?',
  info: 'Reliability, security and continuity are crucial in the financial sector. Our hosting includes standard maintenance, security, backups and monitoring.',
  answers: [{
    text: 'Volledig ontzorgd via Noord Digital (aanbevolen)',
    info: 'We take care of hosting, maintenance, security, backups and monitoring.'
  }, {
    text: 'Externe hosting met technische ondersteuning',
    info: 'U host extern, wij bieden technische support bij problemen'
  }, {
    text: 'Alles zelf regelen',
    info: 'U regelt hosting en onderhoud volledig zelfstandig'
  }]
}, {
  id: Q_TIMELINE,
  question: 'Wanneer wilt u de website live hebben?',
  info: 'De gewenste planning bepaalt de projectintensiteit en prioriteit. Kortere deadlines vragen om een strakkere samenwerking.',
  answers: [{
    text: 'Geen haast',
    info: 'Flexibele planning, kwaliteit boven snelheid'
  }, {
    text: 'Binnen 2–3 maanden',
    info: 'Normale doorlooptijd met ruimte voor iteraties'
  }, {
    text: 'Binnen 4–6 weken',
    info: 'Versnelde planning met intensieve samenwerking'
  }, {
    text: 'Zo snel mogelijk',
    info: 'Spoedproject met hoge prioriteit'
  }]
}, {
  id: Q_PRIORITY,
  question: 'Wat vindt u belangrijker bij de keuze voor een website?',
  info: 'Een website is een langetermijninvestering. Door uw prioriteit te bepalen, kunnen wij een passende prijsindicatie geven.',
  answers: [{
    text: 'Maximale kwaliteit, zekerheid en ontzorging',
    info: 'Kwaliteit en service zijn belangrijker dan prijs'
  }, {
    text: 'Goede balans tussen prijs en kwaliteit',
    info: 'Optimale prijs-kwaliteitverhouding'
  }, {
    text: 'Zo laag mogelijke prijs',
    info: 'Budget is de belangrijkste factor'
  }]
}];
export const PricingScan: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [otherInputs, setOtherInputs] = useState<Record<number, string>>({});
  const [showInfo, setShowInfo] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hoveredAnswer, setHoveredAnswer] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    name: '',
    phone: ''
  });
  const [consent, setConsent] = useState({
    privacy: false,
    marketing: false
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [expandedFeature, setExpandedFeature] = useState<number | null>(null);
  const currentQuestion = currentStep >= 0 && currentStep < questions.length ? questions[currentStep] : null;
  const isMultiSelect = currentQuestion?.multiSelect || false;
  const currentAnswers = currentStep >= 0 ? answers[currentStep] || [] : [];
  const hasOtherSelected = currentAnswers.includes('Overig') || currentAnswers.includes('Anders');
  const handleAnswerSelect = (answer: string) => {
    if (isMultiSelect) {
      const newAnswers = currentAnswers.includes(answer) ? currentAnswers.filter(a => a !== answer) : [...currentAnswers, answer];
      setAnswers({
        ...answers,
        [currentStep]: newAnswers
      });
    } else {
      setAnswers({
        ...answers,
        [currentStep]: [answer]
      });
    }
  };
  const canProceed = currentStep === -1 ? formData.company.trim() !== '' : currentAnswers.length > 0;
  const getNextStepIndex = (currentIndex: number) => {
    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length) {
      const question = questions[nextIndex];
      if (!question.condition || question.condition(answers)) {
        return nextIndex;
      }
      nextIndex++;
    }
    return -1;
  };
  const getPrevStepIndex = (currentIndex: number) => {
    let prevIndex = currentIndex - 1;
    while (prevIndex >= 0) {
      const question = questions[prevIndex];
      if (!question.condition || question.condition(answers)) {
        return prevIndex;
      }
      prevIndex--;
    }
    return -1;
  };
  const handleNext = () => {
    const nextIndex = getNextStepIndex(currentStep);
    if (nextIndex !== -1) {
      setCurrentStep(nextIndex);
      setShowInfo(false);
      setHoveredAnswer(null);
    } else {
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (showResults) {
      setShowResults(false);
      return;
    }
    if (currentStep === -1) return;
    const prevIndex = getPrevStepIndex(currentStep);
    setCurrentStep(prevIndex);
    setShowInfo(false);
    setHoveredAnswer(null);
  };
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (consent.privacy) {
      setContactSubmitted(true);
      console.log('Form submitted:', {
        ...formData,
        answers,
        consent
      });
    }
  };
  const calculatePricing = () => {
    let oneTimeMin = 0;
    let oneTimeMax = 0;
    let monthlyMin = 0;
    let monthlyMax = 0;
    let yearlyMin = 149;
    const websiteType = answers[Q_WEBSITE_TYPE]?.[0];
    const orgSize = answers[Q_ORG_SIZE]?.[0];
    const pages = answers[Q_PAGES] || [];
    const pageCount = pages.length;
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const content = answers[Q_CONTENT]?.[0];
    const priority = answers[Q_PRIORITY]?.[0];
    const timeline = answers[Q_TIMELINE]?.[0];
    if (websiteType?.includes('Onepager')) {
      oneTimeMin = 1000;
      oneTimeMax = 3000;
    } else if (websiteType?.includes('Webshop')) {
      if (orgSize?.includes('Zelfstandig') || orgSize?.includes('2–5')) {
        oneTimeMin = 5000;
        oneTimeMax = 7000;
      } else if (orgSize?.includes('6–15')) {
        oneTimeMin = 7000;
        oneTimeMax = 12000;
      } else {
        oneTimeMin = 12000;
        oneTimeMax = 20000;
      }
    } else {
      oneTimeMin = 3000;
      oneTimeMax = 5000;
      if (pageCount > 5) {
        oneTimeMin += 1000;
        oneTimeMax += 2000;
      }
      if (pageCount > 8) {
        oneTimeMin += 1500;
        oneTimeMax += 2500;
      }
      if (pages.some(p => p.includes('Klantportaal'))) {
        oneTimeMin += 2500;
        oneTimeMax += 4500;
      }
      if (pages.some(p => p.includes('Blog'))) {
        oneTimeMin += 800;
        oneTimeMax += 1500;
      }
    }
    if (!websiteType?.includes('Onepager')) {
      if (orgSize?.includes('15+')) {
        oneTimeMin += 3000;
        oneTimeMax += 5000;
      }
    }
    if (seo?.includes('Zeer belangrijk')) {
      oneTimeMin += 1500;
      oneTimeMax += 2500;
    } else if (seo?.includes('Belangrijk')) {
      oneTimeMin += 800;
      oneTimeMax += 1500;
    }
    if (content?.includes('Volledig laten schrijven')) {
      oneTimeMin += 2000;
      oneTimeMax += 3500;
    } else if (content?.includes('Samen herschrijven')) {
      oneTimeMin += 1000;
      oneTimeMax += 1800;
    }
    if (priority?.includes('Maximale kwaliteit')) {
      oneTimeMin *= 1.2;
      oneTimeMax *= 1.2;
    }
    if (timeline?.includes('Zo snel mogelijk')) {
      oneTimeMin += 1000;
      oneTimeMax += 2000;
    }
    if (hosting?.includes('Volledig ontzorgd')) {
      if (websiteType?.includes('Onepager')) {
        monthlyMin = 25;
        monthlyMax = 45;
      } else if (websiteType?.includes('Webshop')) {
        monthlyMin = 150;
        monthlyMax = 300;
      } else {
        if (pageCount <= 5) {
          monthlyMin = 75;
          monthlyMax = 125;
        } else {
          monthlyMin = 125;
          monthlyMax = 200;
        }
      }
      if (pages.some(p => p.includes('Klantportaal'))) {
        monthlyMin += 50;
        monthlyMax += 100;
      }
    } else if (hosting?.includes('Externe hosting')) {
      monthlyMin = 35;
      monthlyMax = 75;
      yearlyMin += 400;
    } else {
      yearlyMin += 600;
    }
    oneTimeMin = Math.round(oneTimeMin / 100) * 100;
    oneTimeMax = Math.round(oneTimeMax / 100) * 100;
    return {
      oneTime: {
        min: oneTimeMin,
        max: oneTimeMax
      },
      monthly: {
        min: monthlyMin,
        max: monthlyMax
      },
      yearly: {
        min: yearlyMin
      }
    };
  };
  const getIncludedFeatures = () => {
    const features: Array<{
      text: string;
      tooltip: string;
    }> = [];
    const websiteType = answers[Q_WEBSITE_TYPE]?.[0];
    const pages = answers[Q_PAGES] || [];
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const content = answers[Q_CONTENT]?.[0];
    // TOP PRIORITY ITEMS - Always first
    features.push({
      text: 'Persoonlijke begeleiding & advies',
      tooltip: 'Directe toegang tot je vaste contactpersoon voor strategisch advies, sparren over keuzes en begeleiding door het hele proces.'
    });
    features.push({
      text: '100% tevredenheidsgarantie',
      tooltip: 'Unlimited revisions until you are fully satisfied, money-back guarantee if we do not meet your expectations, and personal guidance throughout the entire process.'
    });
    // CORE WEBSITE FEATURES
    if (websiteType?.includes('Onepager')) {
      features.push({
        text: 'Professionele Onepager / Landingspagina',
        tooltip: 'Eén krachtige pagina die je boodschap helder overbrengt en bezoekers converteert naar klanten.'
      });
    } else if (websiteType?.includes('Webshop')) {
      features.push({
        text: 'Complete Webshop functionaliteit',
        tooltip: 'Volledig werkende webshop met productbeheer, voorraadadministratie en betalingsintegratie.'
      });
      features.push({
        text: 'Productbeheer & Categorieën',
        tooltip: 'Eenvoudig producten toevoegen, bewerken en organiseren in overzichtelijke categorieën.'
      });
      features.push({
        text: 'Winkelwagen & Checkout',
        tooltip: 'Geoptimaliseerd bestelproces met veilige betalingsmogelijkheden en orderbevestigingen.'
      });
    } else {
      features.push({
        text: `Website met ± ${Math.max(pages.length, 4)} pagina's`,
        tooltip: `Professionele meerpagins website met ${Math.max(pages.length, 4)} pagina's, inclusief navigatie en logische structuur.`
      });
    }
    // HOSTING & TECHNICAL
    if (hosting?.includes('Volledig ontzorgd')) {
      features.push({
        text: 'Hosting & onderhoud',
        tooltip: 'Betrouwbare hosting op snelle servers met dagelijkse back-ups en proactief onderhoud.'
      });
      features.push({
        text: 'Beveiliging & dagelijkse back-ups',
        tooltip: 'SSL certificate, firewall, malware scanning and automatic daily backups for maximum security.'
      });
      features.push({
        text: 'Monitoring & uptime garantie',
        tooltip: '24/7 monitoring van je website met directe alerts bij problemen en 99.9% uptime garantie.'
      });
    }
    // SEO CONFIGURATION - Check for Premium (second-best and best)
    const isVeryHighSEO = seo?.includes('Zeer belangrijk');
    const isHighSEO = seo?.includes('Belangrijk');
    if (isVeryHighSEO || isHighSEO) {
      features.push({
        text: 'Yoast Premium-configuratie',
        tooltip: 'Geavanceerde SEO-instellingen met Yoast Premium voor optimale vindbaarheid in zoekmachines.'
      });
    } else {
      features.push({
        text: 'Basis Yoast SEO-configuratie',
        tooltip: 'Standaard SEO-optimalisatie met Yoast SEO voor goede vindbaarheid in Google.'
      });
    }
    features.push({
      text: 'Responsive design (mobiel & desktop)',
      tooltip: 'Your website looks perfect on all devices: smartphone, tablet, and desktop.'
    });
    features.push({
      text: 'AVG-compliant privacy instellingen',
      tooltip: 'Volledige naleving van AVG-wetgeving met cookiemelding, privacyverklaring en gegevensbescherming.'
    });
    // SPECIAL FEATURES
    if (pages.some(p => p.includes('Klantportaal'))) {
      features.push({
        text: 'Beveiligd klantportaal',
        tooltip: 'Afgeschermde omgeving waar klanten kunnen inloggen voor documenten, gegevens of persoonlijke informatie.'
      });
    }
    if (pages.some(p => p.includes('Blog')) || websiteType?.includes('Webshop')) {
      features.push({
        text: 'Blog/kennisbank functionaliteit',
        tooltip: 'Publiceer eenvoudig artikelen, nieuws en kennisitems om autoriteit op te bouwen en SEO te verbeteren.'
      });
    }
    if (isVeryHighSEO || isHighSEO) {
      features.push({
        text: 'Uitgebreide SEO-optimalisatie',
        tooltip: 'Deep keyword analysis, technical SEO, speed optimization and structured data for maximum findability.'
      });
    }
    if (content?.includes('Volledig laten schrijven')) {
      features.push({
        text: 'Professioneel geschreven content',
        tooltip: 'Alle teksten worden door ervaren copywriters geschreven, afgestemd op jouw doelgroep en geoptimaliseerd voor conversie en SEO.'
      });
    }
    features.push({
      text: 'Onbeperkte revisies tijdens ontwikkeling',
      tooltip: 'Zoveel aanpassingen als nodig tijdens het ontwikkelproces tot je volledig tevreden bent met het resultaat.'
    });
    // TRAINING - Separate item at the end
    features.push({
      text: 'Training',
      tooltip: 'Persoonlijke training in het gebruik van je website, zodat je zelfstandig content kunt beheren en aanpassen.'
    });
    return features;
  };
  // FIRST: Check if showing results
  if (showResults) {
    const pricing = calculatePricing();
    const includedFeatures = getIncludedFeatures();
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const isHighSEO = seo?.includes('Zeer belangrijk');
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center p-4 py-12" data-id="element-429">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6
      }} className="w-full max-w-5xl" data-id="element-430">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8" data-id="element-431">
            <div className="bg-gradient-to-br from-[#007AFF] to-[#005FCC] p-8 md:p-12 text-white" data-id="element-432">
              <div className="max-w-3xl mx-auto text-center" data-id="element-433">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight" data-id="element-434">
                  Persoonlijk plan voor {formData.company}
                </h1>
                <p className="text-blue-100 text-xl mb-8 leading-relaxed" data-id="element-435">
                  Op basis van uw antwoorden
                </p>

                <div className="border-t border-blue-300 opacity-30 my-6" data-id="element-436"></div>

                <p className="text-blue-100 text-sm font-bold mb-3 uppercase tracking-wider" data-id="element-437">
                  Eenmalige investering
                </p>
                <div className="text-5xl md:text-6xl font-bold mb-4" data-id="element-438">
                  €{pricing.oneTime.min.toLocaleString()} – €
                  {pricing.oneTime.max.toLocaleString()}
                </div>
                <p className="text-blue-50 text-lg" data-id="element-439">
                  Websitebouw, design, maatwerk en volledige implementatie
                </p>
              </div>
            </div>

            <div className="p-8 md:p-12" data-id="element-440">
              <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-441">
                {pricing.monthly.min > 0 && <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border-2 border-blue-100" data-id="element-442">
                    <p className="text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide" data-id="element-443">
                      Maandelijkse kosten
                    </p>
                    <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-444">
                      €{pricing.monthly.min} – €{pricing.monthly.max}{' '}
                      <span className="text-lg font-normal text-gray-500" data-id="element-445">
                        p/m
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-446">
                      {hosting?.includes('Volledig ontzorgd') ? `Hosting, onderhoud, beveiliging, back-ups en monitoring${isHighSEO ? '. Inclusief SEO-optimalisatie' : ''}` : 'Technische ondersteuning en begeleiding'}
                    </p>
                  </div>}

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-200" data-id="element-447">
                  <p className="text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide" data-id="element-448">
                    Jaarlijkse kosten
                  </p>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-449">
                    Vanaf €{pricing.yearly.min}{' '}
                    <span className="text-lg font-normal text-gray-500" data-id="element-450">
                      p/j
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-451">
                    Yoast SEO Premium licentie
                    {!hosting?.includes('Volledig ontzorgd') && ' + aanbevolen optimalisatie'}
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-5 mb-10" data-id="element-452">
                <div className="flex items-start" data-id="element-453">
                  <InfoIcon className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-454" />
                  <div data-id="element-455">
                    <p className="text-gray-900 font-semibold mb-1" data-id="element-456">
                      Dit is een indicatie
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed" data-id="element-457">
                      In de praktijk werken we deze prijs vaak naar beneden bij
                      op basis van slimme keuzes en efficiënte werkwijzen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-12" data-id="element-458">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-459">
                  Wat is inbegrepen?
                </h3>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-460">
                  {includedFeatures.map((feature, index) => <div key={index} className="relative" data-id="element-461">
                      <div className="flex items-start bg-white rounded-lg p-3 shadow-sm" data-id="element-462">
                        <div className="flex-shrink-0 w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center mr-3 mt-0.5" data-id="element-463">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-464" />
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-relaxed flex-1" data-id="element-465">
                          {feature.text}
                        </span>
                        <button onClick={() => setExpandedFeature(expandedFeature === index ? null : index)} className="flex-shrink-0 ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors" aria-label="Meer informatie" data-id="element-466">
                          <InfoIcon className="h-4 w-4 text-gray-400 hover:text-[#007AFF]" data-id="element-467" />
                        </button>
                      </div>
                      <AnimatePresence data-id="element-468">
                        {expandedFeature === index && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-469">
                            <div className="mt-2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl" data-id="element-470">
                              {feature.tooltip}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </div>
              </div>

              <div className="border-t-2 border-gray-100 pt-10" data-id="element-471">
                {!contactSubmitted ? <div className="max-w-2xl mx-auto" data-id="element-472">
                    <div className="text-center mb-8" data-id="element-473">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-474">
                        Ontvang uw persoonlijke offerte
                      </h3>
                      <p className="text-gray-600 text-lg mb-4" data-id="element-475">
                        Laat uw gegevens achter en we nemen vrijblijvend contact
                        met u op om deze indicatie toe te lichten.
                      </p>
                      <div className="bg-blue-50 border-l-4 border-[#007AFF] rounded-r-xl p-4 text-left" data-id="element-476">
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-477">
                          <strong className="text-[#007AFF]" data-id="element-478">
                            Geen verkooppraatjes:
                          </strong>{' '}
                          Noord Digital neemt éénmalig contact met u op om een
                          vrijblijvende offerte te versturen. U wordt niet
                          ingeschreven voor een nieuwsbrief en ontvangt geen
                          verkoopgesprekken.
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleContactSubmit} className="space-y-6" data-id="element-479">
                      <div className="grid md:grid-cols-2 gap-4" data-id="element-480">
                        <div data-id="element-481">
                          <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-482">
                            Naam
                          </label>
                          <input type="text" required value={formData.name} onChange={e => setFormData({
                        ...formData,
                        name: e.target.value
                      })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="Uw naam" data-id="element-483" />
                        </div>
                        <div data-id="element-484">
                          <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-485">
                            E-mailadres <span className="text-red-500" data-id="element-486">*</span>
                          </label>
                          <input type="email" required value={formData.email} onChange={e => setFormData({
                        ...formData,
                        email: e.target.value
                      })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="uw@email.nl" data-id="element-487" />
                        </div>
                      </div>

                      <div data-id="element-488">
                        <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-489">
                          Telefoonnummer{' '}
                          <span className="text-gray-400 font-normal" data-id="element-490">
                            (optioneel)
                          </span>
                        </label>
                        <input type="tel" value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="06 12345678" data-id="element-491" />
                      </div>

                      <div className="bg-blue-50 rounded-xl p-4" data-id="element-492">
                        <label className="flex items-start cursor-pointer" data-id="element-493">
                          <input type="checkbox" checked={consent.privacy} onChange={e => setConsent({
                        ...consent,
                        privacy: e.target.checked
                      })} className="mt-1 h-5 w-5 text-[#007AFF] border-2 border-gray-300 rounded focus:ring-2 focus:ring-[#007AFF] cursor-pointer" required data-id="element-494" />
                          <span className="ml-3 text-sm text-gray-800 leading-relaxed" data-id="element-495">
                            <strong className="text-red-500" data-id="element-496">*</strong> Ik ga
                            akkoord met het verwerken van mijn gegevens zoals
                            beschreven in de{' '}
                            <a href="/privacy" target="_blank" className="text-[#007AFF] hover:underline font-semibold" data-id="element-497">
                              privacyverklaring
                            </a>
                            .
                          </span>
                        </label>
                      </div>

                      <motion.button whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} type="submit" disabled={!consent.privacy || !formData.name} className="w-full flex items-center justify-center px-6 py-4 rounded-xl bg-gradient-to-r from-[#007AFF] to-[#005FCC] hover:from-[#005FCC] hover:to-[#004BA8] text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl" data-id="element-498">
                        Verstuur aanvraag
                        <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-499" />
                      </motion.button>
                    </form>
                  </div> : <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border-2 border-green-100 text-center max-w-2xl mx-auto" data-id="element-500">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-501">
                      <CheckIcon className="h-8 w-8 text-green-600" data-id="element-502" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-503">
                      Bedankt voor uw aanvraag!
                    </h3>
                    <p className="text-gray-600" data-id="element-504">
                      We hebben uw gegevens ontvangen en nemen zo snel mogelijk
                      contact met u op.
                    </p>
                  </motion.div>}
              </div>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  // SECOND: Check if on company info step
  if (currentStep === -1) {
    return <div className="min-h-screen bg-gradient-to-br from-[#F7F8FA] to-white flex items-center justify-center p-4" data-id="element-505">
        <div className="w-full max-w-2xl relative" data-id="element-506">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.5
        }} className="hidden lg:block absolute -left-40 top-20 w-32" data-id="element-507">
            <div className="bg-white border-2 border-[#007AFF] text-[#007AFF] p-3 rounded-xl rounded-br-none shadow-sm text-sm font-bold mb-2 text-center" data-id="element-508">
              Ik help je met de scan!
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte - kosten professionele website accountantskantoor" className="w-24 h-24 object-contain rounded-full border-4 border-white shadow-md mx-auto" data-id="element-509" />
          </motion.div>

          <div className="mb-8" data-id="element-510">
            <div className="flex items-center justify-between mb-2" data-id="element-511">
              <span className="text-sm font-semibold text-[#111827]" data-id="element-512">
                Stap 1 van {questions.length + 1}
              </span>
              <span className="text-sm text-[#6B7280]" data-id="element-513">
                {Math.round(1 / (questions.length + 1) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-514">
              <motion.div className="bg-[#007AFF] h-full" initial={{
              width: 0
            }} animate={{
              width: `${1 / (questions.length + 1) * 100}%`
            }} transition={{
              duration: 0.5
            }} data-id="element-515" />
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-516">
            <div className="text-center mb-8" data-id="element-517">
              <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-4" data-id="element-518">
                Welkom bij de Website Scan
              </h2>
              <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-519">
                Vul eerst uw bedrijfsgegevens in om te starten met de scan
              </p>
            </div>

            <form className="space-y-6" data-id="element-520">
              <div data-id="element-521">
                <label className="block text-sm font-bold text-[#111827] mb-2" data-id="element-522">
                  Bedrijfsnaam <span className="text-red-500" data-id="element-523">*</span>
                </label>
                <input type="text" required value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="Naam van uw organisatie" data-id="element-524" />
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} type="button" onClick={handleNext} disabled={!canProceed} className="w-full flex items-center justify-center px-6 py-4 rounded-xl bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl" data-id="element-525">
                Start de scan
                <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-526" />
              </motion.button>

              <p className="text-xs text-[#6B7280] text-center leading-relaxed" data-id="element-527">
                Wij behandelen uw gegevens vertrouwelijk conform onze{' '}
                <a href="/privacy" className="text-[#007AFF] hover:underline" data-id="element-528">
                  privacyverklaring
                </a>
              </p>
            </form>
          </motion.div>
        </div>
      </div>;
  }
  // THIRD: Questions flow (default)
  return <div className="min-h-screen bg-gradient-to-br from-[#F7F8FA] to-white flex items-center justify-center p-4" data-id="element-529">
      <div className="w-full max-w-3xl relative" data-id="element-530">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5
      }} className="hidden lg:block absolute -left-40 top-32" data-id="element-531">
          <motion.img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte - Website scan uitleg" className="w-24 h-24 object-contain rounded-full border-4 border-white shadow-md mx-auto" data-id="element-532" />

          <div className="text-center mt-2 text-xs text-[#6B7280] font-medium" data-id="element-533">
            Hover voor uitleg
          </div>
        </motion.div>

        <div className="mb-8" data-id="element-534">
          <div className="flex items-center justify-between mb-2" data-id="element-535">
            <span className="text-sm font-semibold text-[#111827]" data-id="element-536">
              Stap {currentStep + 2} van {questions.length + 1}
            </span>
            <span className="text-sm text-[#6B7280]" data-id="element-537">
              {Math.round((currentStep + 2) / (questions.length + 1) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-538">
            <motion.div className="bg-[#007AFF] h-full" initial={{
            width: `${(currentStep + 1) / (questions.length + 1) * 100}%`
          }} animate={{
            width: `${(currentStep + 2) / (questions.length + 1) * 100}%`
          }} transition={{
            duration: 0.5
          }} data-id="element-539" />
          </div>
        </div>

        <motion.div key={currentStep} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-540">
          <div className="flex items-start justify-between mb-6" data-id="element-541">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] pr-4" data-id="element-542">
              {currentQuestion?.question}
            </h2>
            <button onClick={() => setShowInfo(!showInfo)} className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[#F7F8FA] hover:bg-[#007AFF] hover:text-white transition-colors" aria-label="Meer informatie" data-id="element-543">
              <InfoIcon className="h-5 w-5" data-id="element-544" />
            </button>
          </div>

          <AnimatePresence data-id="element-545">
            {showInfo && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="bg-[#E6F2FF] border-l-4 border-[#007AFF] rounded-lg p-4 mb-6 overflow-hidden" data-id="element-546">
                <p className="text-[#111827] text-sm leading-relaxed" data-id="element-547">
                  {currentQuestion?.info}
                </p>
              </motion.div>}
          </AnimatePresence>

          {isMultiSelect && <p className="text-sm text-[#6B7280] mb-4 italic" data-id="element-548">
              Meerdere antwoorden mogelijk
            </p>}

          <div className="space-y-3 mb-6" data-id="element-549">
            {currentQuestion?.answers.map((answer, index) => {
            const isSelected = currentAnswers.includes(answer.text);
            const isHovered = hoveredAnswer === answer.text;
            return <div key={index} className="relative" data-id="element-550">
                  <motion.button whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} onClick={() => handleAnswerSelect(answer.text)} onMouseEnter={() => setHoveredAnswer(answer.text)} onMouseLeave={() => setHoveredAnswer(null)} onMouseMove={e => {
                setMousePosition({
                  x: e.clientX,
                  y: e.clientY
                });
              }} className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-[#007AFF] bg-[#E6F2FF] shadow-md' : 'border-gray-200 hover:border-[#007AFF] hover:bg-[#F7F8FA]'}`} data-id="element-551">
                    <div className="flex items-center justify-between" data-id="element-552">
                      <span className={`font-medium ${isSelected ? 'text-[#007AFF]' : 'text-[#111827]'}`} data-id="element-553">
                        {answer.text}
                      </span>
                      {isSelected && <CheckIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-554" />}
                    </div>
                  </motion.button>
                </div>;
          })}
          </div>

          <AnimatePresence data-id="element-555">
            {hoveredAnswer && currentQuestion?.answers.find(a => a.text === hoveredAnswer)?.info && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} style={{
            position: 'fixed',
            left: mousePosition.x + 20,
            top: mousePosition.y - 20,
            pointerEvents: 'none',
            zIndex: 9999
          }} className="bg-[#111827] text-white text-sm rounded-lg p-3 shadow-xl max-w-xs" data-id="element-556">
                  <p className="leading-relaxed" data-id="element-557">
                    {currentQuestion?.answers.find(a => a.text === hoveredAnswer)?.info}
                  </p>
                </motion.div>}
          </AnimatePresence>

          {currentQuestion?.showOtherInput && hasOtherSelected && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="mb-6" data-id="element-558">
              <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-559">
                Specificeer{' '}
                {currentStep === 0 ? 'uw organisatietype' : '(optioneel)'}
              </label>
              <input type="text" value={otherInputs[currentStep] || ''} onChange={e => setOtherInputs({
            ...otherInputs,
            [currentStep]: e.target.value
          })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition" placeholder={currentStep === 0 ? 'bijv. Belastingadviseur, Pensioenadviseur...' : 'Vul hier uw specificatie in...'} data-id="element-560" />
            </motion.div>}

          <div className="flex gap-4" data-id="element-561">
            <button onClick={handlePrevious} className="flex items-center justify-center px-6 py-3 rounded-lg border-2 border-gray-300 text-[#111827] font-semibold hover:bg-gray-50 transition" data-id="element-562">
              <ChevronLeftIcon className="h-5 w-5 mr-2" data-id="element-563" />
              Vorige
            </button>
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={handleNext} disabled={!canProceed} className="flex-1 flex items-center justify-center px-6 py-3 rounded-lg bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg" data-id="element-564">
              {currentStep === questions.length - 1 ? 'Bekijk resultaat' : 'Volgende'}
              <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-565" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>;
};